import React, { useEffect, useState } from "react";
import { Card, Tab, Nav, Modal, Spinner } from "react-bootstrap";
import { ArrangeHorizontal, Sms, Call, InfoCircle } from "iconsax-react";
import { Link, useLocation, useParams } from 'react-router-dom'
import DatiAnagraficiPlayer from "../../../../components/Clienti/datiAnagrafici_player";
import DatiSportPlayer from "../../../../components/Clienti/datiSportivi_player";
import DatiSanPlayer from "../../../../components/Clienti/datiSanitari_player";
import FormaFisPlayer from "../../../../components/Clienti/formaFisica_player";
import TraguardiPlayer from "../../../../components/Clienti/traguardi_player";
import JuniorPlayer from "../../../../components/Clienti/junior_player";
import TesseramentiPlayer from "../../../../components/Clienti/tesseramenti_player";
import MyApiClient from "../../../../../api-client/MyApiClient";
import DynamicLogo from "../../../DynamicLogoLoader";
import { tipologiaStruttura } from '../../../../components/Util/enum';
import { usePageTitle, useStatoInSolaLettura } from "../../../../layouts/PageHeaderExtra";

const STATUS_MAP = {
	ACTIVE: ['Attivo', 'success'],
	COMPLETED: ['Completato', 'success'],
	INACTIVE: ['Inattivo', 'danger'],
	INVITED: ['Invito in corso', 'info'],
};

export const PlayerProfile = () => {

	const { id } = useParams();

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [profile, setProfile] = useState(null);
	const [privateProfile, setPrivateProfile] = useState(null);
	const [labelNome, setLabelNome] = useState('');
	// Titolo della pagina = nome del cliente; finché non è caricato resta quello generico
	usePageTitle(labelNome);

	let clubNature = null;
	const tokenDetailsString = sessionStorage.getItem('userDetails');
	if (tokenDetailsString) {
		clubNature = JSON.parse(tokenDetailsString).clubNature;
	}

	let showTesserati = false;
	if (clubNature === tipologiaStruttura.ASD.value || clubNature === tipologiaStruttura.SSD.value) {
		showTesserati = true;
	}

	const tabData = [
		{
			id: 'dati-anagrafici',
			name: "Anagrafica",
			content: <DatiAnagraficiPlayer profile={profile} privateProfile={privateProfile} playerId={id} />,
		},
		{
			id: 'datisportivi',
			name: "Sport",
			content: <DatiSportPlayer profile={profile} privateProfile={privateProfile} />,
		},
		{
			id: 'datisanitari',
			name: "Dati sanitari",
			content: <DatiSanPlayer profile={profile} privateProfile={privateProfile} />,
		},
		{
			id: 'formafisica',
			name: "Forma fisica",
			content: <FormaFisPlayer profile={profile} privateProfile={privateProfile} />,
		},
		{
			id: 'traguardi',
			name: "Traguardi",
			content: <TraguardiPlayer profile={profile} privateProfile={privateProfile} />,
		},
		{
			id: 'junior',
			name: "Junior",
			content: <JuniorPlayer profile={profile} privateProfile={privateProfile} />,
		},
		...(showTesserati ? [{
			id: 'tesseramenti',
			name: "Tesseramenti",
			content: <TesseramentiPlayer profile={profile} privateProfile={privateProfile} userId={id} />,
		}] : []),
	];

	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const [defaultTab, setDefaultTab] = useState(query.get('tab') || tabData[0].id.toLowerCase());

	useEffect(() => {
		const tab = new URLSearchParams(location.search).get('tab');
		if (tab && tab !== defaultTab) {
			setDefaultTab(tab);
		}
	}, [location.search, defaultTab]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const tokenDetailsStr = sessionStorage.getItem('userDetails');
				if (tokenDetailsStr) {
					const token = JSON.parse(tokenDetailsStr).idToken;
					const apiClient = new MyApiClient();

					setLoadingShowed(true);
					// Il profilo personale e` un di piu`: se non arriva, la scheda si apre
					// lo stesso. Senza questo `catch` un suo errore faceva fallire
					// l'intera Promise.all e la pagina restava sullo spinner.
					const [profileData, privateProfileData] = await Promise.all([
						apiClient.getProfiles(token, id),
						apiClient.getPrivateProfile(token, id).catch(() => null)
					]);

					let labelNomeTemp = '';
					if (profileData.idsPlay) {
						labelNomeTemp = profileData.idsPlay.givenName + ' ' + profileData.idsPlay.familyName;
					} else if (profileData.idsGuru) {
						profileData.idsPlay = profileData.idsGuru;
						labelNomeTemp = profileData.idsPlay.givenName + ' ' + profileData.idsPlay.familyName;
					}
					setLabelNome(labelNomeTemp);

					setProfile(profileData);
					setPrivateProfile(privateProfileData);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				// Lo spinner si chiude comunque sia andata: e` una finestra modale, e
				// lasciarla aperta blocca la pagina.
				setLoadingShowed(false);
			}
		};

		fetchData();
	}, [id]);

	const p = profile && profile.idsPlay;
	const initials = p ? `${(p.givenName || '')[0] || ''}${(p.familyName || '')[0] || ''}` : '';
	const [statusLabel, statusVariant] = (p && STATUS_MAP[p.profileStatus]) || ['', 'muted'];
	// Stato del profilo in sola lettura subito dopo il titolo (manca l'API per cambiarlo)
	useStatoInSolaLettura(statusLabel, statusVariant === 'success' ? 'attivo' : ['danger', 'warning'].includes(statusVariant) ? 'non-attivo' : 'bozza');

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			{/* Header profilo */}
			<Card className="mb-4">
				<Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-3">
					<div className="d-flex align-items-center gap-3">
						<div className="sid-avatar-lg">
							{p && p.picture ? <DynamicLogo logoUrl={p.picture} /> : initials}
						</div>
						<div>
							<h4 className="mb-1">{labelNome}</h4>
							<div className="d-flex align-items-center gap-3 flex-wrap">
								{p && p.email && <span className="d-inline-flex align-items-center gap-1 text-muted"><Sms size={16} variant="Bulk" /> {p.email}</span>}
								{p && p.phoneNumber && <span className="d-inline-flex align-items-center gap-1 text-muted"><Call size={16} variant="Bulk" /> {p.phoneNumber}</span>}
							</div>
							{profile && profile.idsGuru && (
								<Link to={'/struttura/maestri/maestro_profile/' + profile.idsGuru.userId} className="btn btn-sm btn-booking mt-2">
									<ArrangeHorizontal variant="Bulk" /><span className='ms-2'>Visualizza profilo Maestro</span>
								</Link>
							)}
						</div>
					</div>
					<div className="sid-readonly-note">
						<InfoCircle size={20} variant="Bulk" />
						<span><strong>Dati in sola lettura.</strong> Modificabili solo dal Player tramite l'app Sportid Player.</span>
					</div>
				</Card.Body>
			</Card>

			{/* Tabs */}
			<div className="custom-tab-1">
				<Tab.Container defaultActiveKey={defaultTab}>
					<Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
						{tabData.map((data, i) => (
							<Nav.Item as="li" key={i} className="tabMenu">
								<Nav.Link eventKey={data.id.toLowerCase()}>
									{data.name}
								</Nav.Link>
							</Nav.Item>
						))}
					</Nav>
					<Tab.Content className="mt-4">
						{tabData.map((data, i) => (
							<Tab.Pane eventKey={data.id.toLowerCase()} key={i}>
								{data.content}
							</Tab.Pane>
						))}
					</Tab.Content>
				</Tab.Container>
			</div>
		</>
	)
}

export default PlayerProfile;
